import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, SimpleGrid, HStack, VStack, Text, Icon, Spinner, Badge, Divider, Flex, Center,
  Stat, StatLabel, StatNumber, StatHelpText, Table, Thead, Tbody, Tr, Th, Td,
  Input, Button, Wrap, WrapItem,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
} from '@chakra-ui/react';
import {
  MdGroups, MdVolunteerActivism, MdAssignmentTurnedIn, MdCardGiftcard,
  MdHistory, MdPlace, MdFilterAlt, MdClose,
} from 'react-icons/md';
import { IconType } from 'react-icons';
import axiosInstance from '../../../api/AxiosInstance';

const ROJO = '#5b182b';
const ROJO_HOVER = '#691f30';

// ─────────── Utilidades ───────────
const fmtFecha = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const enRango = (v: string | null | undefined, desde: string, hasta: string): boolean => {
  if (!desde && !hasta) return true;
  if (!v) return false;
  const t = new Date(v).getTime();
  if (isNaN(t)) return false;
  if (desde && t < new Date(`${desde}T00:00:00`).getTime()) return false;
  if (hasta && t > new Date(`${hasta}T23:59:59`).getTime()) return false;
  return true;
};

const badgeColor = (estatus?: string) =>
  estatus === 'aprobado' ? 'green' : estatus === 'no_aprobado' ? 'red' : 'yellow';

// ─────────── Tarjeta KPI interactiva (drill-down) ───────────
const TarjetaKPI: React.FC<{
  icon: IconType;
  label: string;
  value: React.ReactNode;
  help: string;
  color: string;
  onClick: () => void;
}> = ({ icon, label, value, help, color, onClick }) => (
  <Box
    bg="white"
    borderRadius="lg"
    boxShadow="sm"
    p={4}
    borderLeft={`4px solid ${color}`}
    cursor="pointer"
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    transition="all 0.15s ease"
    _hover={{ boxShadow: 'lg', transform: 'translateY(-3px)' }}
    _active={{ transform: 'translateY(-1px)' }}
  >
    <HStack justify="space-between">
      <Stat>
        <StatLabel color="gray.500" fontSize="xs">{label}</StatLabel>
        <StatNumber color={color}>{value}</StatNumber>
        <StatHelpText fontSize="xs" mb={0}>{help}</StatHelpText>
      </Stat>
      <Icon as={icon} boxSize={9} color={color} opacity={0.85} />
    </HStack>
  </Box>
);

type ModalKey = 'beneficiarios' | 'aprobados' | 'programas' | 'apoyos' | 'movimientos';

const Dashboard: React.FC = () => {
  const [cargando, setCargando] = useState(true);

  // Datos crudos (se cargan una vez; el filtro de fecha es client-side)
  const [beneficiarios, setBeneficiarios] = useState<any[]>([]);
  const [programas, setProgramas] = useState<any[]>([]);
  const [apoyos, setApoyos] = useState<any[]>([]);
  const [movimientos, setMovimientos] = useState<any[]>([]);

  // Filtro de fecha: valores en edición vs. aplicados
  const [desdeInput, setDesdeInput] = useState('');
  const [hastaInput, setHastaInput] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const [modal, setModal] = useState<ModalKey | null>(null);

  useEffect(() => {
    (async () => {
      setCargando(true);
      try {
        const [ben, prog, apo, mov] = await Promise.all([
          axiosInstance.get('/desarrollo-social/beneficiarios').then((r) => r.data || []).catch(() => []),
          axiosInstance.get('/desarrollo-social/programas').then((r) => r.data || []).catch(() => []),
          axiosInstance.get('/desarrollo-social/apoyos').then((r) => r.data || []).catch(() => []),
          axiosInstance.get('/desarrollo-social/movimientos').then((r) => r.data || []).catch(() => []),
        ]);
        // Los beneficiarios ya vienen con isDeleted:false desde el backend; se refuerza aquí.
        setBeneficiarios((ben || []).filter((b: any) => !b.isDeleted));
        setProgramas(prog || []);
        setApoyos((apo || []).filter((a: any) => !a.isDeleted));
        setMovimientos(mov || []);
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  const aplicar = () => { setDesde(desdeInput); setHasta(hastaInput); };
  const limpiar = () => { setDesdeInput(''); setHastaInput(''); setDesde(''); setHasta(''); };
  const hayFiltro = Boolean(desde || hasta);

  // ─────────── Cálculos derivados (recalculan al aplicar el rango) ───────────
  const beneficiariosFilt = useMemo(
    () => beneficiarios.filter((b) => enRango(b.createdAt, desde, hasta)),
    [beneficiarios, desde, hasta],
  );
  const beneficiariosAprobados = useMemo(
    () => beneficiariosFilt.filter((b) => b.estatus === 'aprobado'),
    [beneficiariosFilt],
  );
  const beneficiariosPendientes = useMemo(
    () => beneficiariosFilt.filter((b) => (b.estatus || 'pendiente') === 'pendiente').length,
    [beneficiariosFilt],
  );

  const programasActivos = useMemo(
    () => programas.filter((p) => p.estatus === 'activo' && enRango(p.fechaInicio || p.createdAt, desde, hasta)),
    [programas, desde, hasta],
  );

  const apoyosFilt = useMemo(
    () => apoyos.filter((a) => enRango(a.fechaEntrega, desde, hasta)),
    [apoyos, desde, hasta],
  );

  const movimientosFilt = useMemo(
    () => movimientos.filter((m) => enRango(m.fecha, desde, hasta)),
    [movimientos, desde, hasta],
  );

  // Beneficiarios por localidad (refleja el filtro de fecha)
  const porLocalidad = useMemo(() => {
    const mapa = new Map<string, number>();
    beneficiariosFilt.forEach((b) => {
      const key = b.localidad?.nombre || b.localidadNombre || (b.localidadId != null ? `Localidad ${b.localidadId}` : 'Sin localidad');
      mapa.set(key, (mapa.get(key) || 0) + 1);
    });
    return Array.from(mapa.entries())
      .map(([nombre, total]) => ({ nombre, total }))
      .sort((a, b) => b.total - a.total);
  }, [beneficiariosFilt]);

  // ─────────── Configuración de cada modal (drill-down) ───────────
  const modales: Record<ModalKey, { titulo: string; icon: IconType; head: string[]; filas: () => React.ReactNode[] }> = {
    beneficiarios: {
      titulo: 'Beneficiarios', icon: MdGroups,
      head: ['Folio', 'Nombre', 'CURP', 'Teléfono', 'Estatus', 'Registro'],
      filas: () => beneficiariosFilt.map((b) => (
        <Tr key={b.id}>
          <Td fontSize="xs">{b.folio || '—'}</Td>
          <Td fontSize="xs">{b.nombreCompleto || '—'}</Td>
          <Td fontSize="xs">{b.curp || '—'}</Td>
          <Td fontSize="xs">{b.telefono || '—'}</Td>
          <Td fontSize="xs"><Badge colorScheme={badgeColor(b.estatus)}>{b.estatus || 'pendiente'}</Badge></Td>
          <Td fontSize="xs">{fmtFecha(b.createdAt)}</Td>
        </Tr>
      )),
    },
    aprobados: {
      titulo: 'Beneficiarios aprobados', icon: MdAssignmentTurnedIn,
      head: ['Folio', 'Nombre', 'CURP', 'Teléfono', 'Registro'],
      filas: () => beneficiariosAprobados.map((b) => (
        <Tr key={b.id}>
          <Td fontSize="xs">{b.folio || '—'}</Td>
          <Td fontSize="xs">{b.nombreCompleto || '—'}</Td>
          <Td fontSize="xs">{b.curp || '—'}</Td>
          <Td fontSize="xs">{b.telefono || '—'}</Td>
          <Td fontSize="xs">{fmtFecha(b.createdAt)}</Td>
        </Tr>
      )),
    },
    programas: {
      titulo: 'Programas activos', icon: MdVolunteerActivism,
      head: ['Nombre', 'Tipo', 'Ejercicio', 'Meta', 'Inicio', 'Estatus'],
      filas: () => programasActivos.map((p) => (
        <Tr key={p.id}>
          <Td fontSize="xs">{p.nombre || '—'}</Td>
          <Td fontSize="xs">{p.tipo || '—'}</Td>
          <Td fontSize="xs">{p.ejercicioFiscal || '—'}</Td>
          <Td fontSize="xs" isNumeric>{p.metaProgramada ?? 0}</Td>
          <Td fontSize="xs">{fmtFecha(p.fechaInicio)}</Td>
          <Td fontSize="xs"><Badge colorScheme="green">{p.estatus}</Badge></Td>
        </Tr>
      )),
    },
    apoyos: {
      titulo: 'Apoyos entregados', icon: MdCardGiftcard,
      head: ['Programa', 'Beneficiario', 'Tipo apoyo', 'Cantidad', 'Fecha'],
      filas: () => apoyosFilt.map((a) => (
        <Tr key={a.id}>
          <Td fontSize="xs">{a.programa?.nombre || a.programaId || '—'}</Td>
          <Td fontSize="xs">{a.beneficiario?.nombreCompleto || a.beneficiarioId || '—'}</Td>
          <Td fontSize="xs">{a.tipoApoyo || '—'}</Td>
          <Td fontSize="xs">{a.cantidadEntregada || '—'}</Td>
          <Td fontSize="xs">{fmtFecha(a.fechaEntrega)}</Td>
        </Tr>
      )),
    },
    movimientos: {
      titulo: 'Movimientos', icon: MdHistory,
      head: ['Fecha', 'Entidad', 'Entidad ID', 'Acción', 'Usuario'],
      filas: () => movimientosFilt.map((m) => (
        <Tr key={m.id}>
          <Td fontSize="xs">{m.fecha ? new Date(m.fecha).toLocaleString('es-MX') : '—'}</Td>
          <Td fontSize="xs">{m.entidad || '—'}</Td>
          <Td fontSize="xs">{m.entidadId ?? '—'}</Td>
          <Td fontSize="xs"><Badge colorScheme="purple">{m.accion || '—'}</Badge></Td>
          <Td fontSize="xs">{m.usuarioId || '—'}</Td>
        </Tr>
      )),
    },
  };

  if (cargando) {
    return (
      <VStack spacing={6} align="stretch" px={{ base: 2, md: 4 }} pt={79} pb={4}>
        <Center py={16}><Spinner color={ROJO} size="lg" thickness="3px" /></Center>
        <Text textAlign="center" color="gray.500">Cargando indicadores...</Text>
      </VStack>
    );
  }

  const cfg = modal ? modales[modal] : null;

  return (
    <VStack spacing={5} align="stretch" px={{ base: 2, md: 4 }} pt={79} pb={4}>
      {/* ─────────── Filtro de fecha ─────────── */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} borderTop={`3px solid ${ROJO}`}>
        <Wrap spacing={4} align="flex-end">
          <WrapItem>
            <HStack spacing={2} color={ROJO}>
              <Icon as={MdFilterAlt} />
              <Text fontWeight="bold" fontSize="sm">Filtrar por fecha</Text>
            </HStack>
          </WrapItem>
          <WrapItem>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Desde</Text>
              <Input
                type="date" size="sm" value={desdeInput} maxW="170px"
                onChange={(e) => setDesdeInput(e.target.value)}
                focusBorderColor={ROJO}
              />
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Hasta</Text>
              <Input
                type="date" size="sm" value={hastaInput} maxW="170px"
                onChange={(e) => setHastaInput(e.target.value)}
                focusBorderColor={ROJO}
              />
            </Box>
          </WrapItem>
          <WrapItem>
            <HStack spacing={2}>
              <Button
                size="sm" color="white" bg={ROJO} _hover={{ bg: ROJO_HOVER }}
                leftIcon={<MdFilterAlt />} onClick={aplicar}
              >
                Aplicar
              </Button>
              <Button
                size="sm" variant="outline" color={ROJO} borderColor={ROJO}
                _hover={{ bg: 'gray.50' }} leftIcon={<MdClose />} onClick={limpiar}
                isDisabled={!hayFiltro && !desdeInput && !hastaInput}
              >
                Limpiar
              </Button>
            </HStack>
          </WrapItem>
          {hayFiltro && (
            <WrapItem>
              <Badge bg={ROJO} color="white" px={2} py={1} borderRadius="md" fontSize="2xs">
                Rango: {desde ? fmtFecha(desde) : '…'} – {hasta ? fmtFecha(hasta) : '…'}
              </Badge>
            </WrapItem>
          )}
        </Wrap>
      </Box>

      <Text fontSize="xs" color="gray.400">
        Da clic en cualquier indicador para ver el detalle de los registros que lo componen.
      </Text>

      {/* ─────────── KPIs interactivos ─────────── */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
        <TarjetaKPI icon={MdGroups} label="Beneficiarios" value={beneficiariosFilt.length}
          help={hayFiltro ? 'Registrados en el rango' : `${beneficiarios.length} en total`} color={ROJO}
          onClick={() => setModal('beneficiarios')} />
        <TarjetaKPI icon={MdAssignmentTurnedIn} label="Beneficiarios aprobados" value={beneficiariosAprobados.length}
          help={`${beneficiariosPendientes} pendientes`} color="#2F855A"
          onClick={() => setModal('aprobados')} />
        <TarjetaKPI icon={MdVolunteerActivism} label="Programas activos" value={programasActivos.length}
          help={hayFiltro ? 'En el rango seleccionado' : `${programas.length} en total`} color="#2B6CB0"
          onClick={() => setModal('programas')} />
        <TarjetaKPI icon={MdCardGiftcard} label="Apoyos entregados" value={apoyosFilt.length}
          help={hayFiltro ? 'En el rango seleccionado' : `${apoyos.length} en total`} color="#B7791F"
          onClick={() => setModal('apoyos')} />
        <TarjetaKPI icon={MdHistory} label="Movimientos" value={movimientosFilt.length}
          help={hayFiltro ? 'En el rango seleccionado' : 'Historial de actividad'} color="#6B46C1"
          onClick={() => setModal('movimientos')} />
      </SimpleGrid>

      {/* ─────────── Beneficiarios por localidad (refleja el filtro) ─────────── */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
        <HStack mb={2}><Icon as={MdPlace} color={ROJO} /><Text fontWeight="bold" color={ROJO}>Beneficiarios por localidad</Text></HStack>
        <Divider mb={2} />
        {porLocalidad.length === 0 ? (
          <Text fontSize="sm" color="gray.400" py={4} textAlign="center">Sin beneficiarios{hayFiltro ? ' en el rango seleccionado' : ''}</Text>
        ) : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead><Tr><Th>Localidad</Th><Th isNumeric>Beneficiarios</Th></Tr></Thead>
              <Tbody>
                {porLocalidad.map((l) => (
                  <Tr key={l.nombre}>
                    <Td fontSize="xs">{l.nombre}</Td>
                    <Td fontSize="xs" isNumeric fontWeight="bold" color={ROJO}>{l.total}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {/* ─────────── Modal drill-down ─────────── */}
      <Modal isOpen={!!cfg} onClose={() => setModal(null)} size="4xl" scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg={ROJO} color="white" borderTopRadius="md" py={3}>
            <HStack spacing={2}>
              {cfg && <Icon as={cfg.icon} />}
              <Text fontSize="md">{cfg?.titulo}</Text>
              {cfg && (
                <Badge bg="white" color={ROJO} borderRadius="full" px={2}>
                  {cfg.filas().length}
                </Badge>
              )}
              {hayFiltro && (
                <Text fontSize="xs" opacity={0.85} fontWeight="normal">
                  ({desde ? fmtFecha(desde) : '…'} – {hasta ? fmtFecha(hasta) : '…'})
                </Text>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={4}>
            {(() => {
              if (!cfg) return null;
              const filas = cfg.filas();
              if (filas.length === 0) {
                return (
                  <Flex direction="column" align="center" py={10} color="gray.400">
                    <Icon as={cfg.icon} boxSize={10} mb={2} />
                    <Text fontSize="sm">Sin registros{hayFiltro ? ' en el rango seleccionado' : ''}</Text>
                  </Flex>
                );
              }
              return (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg={ROJO}>
                      <Tr>
                        {cfg.head.map((h) => (
                          <Th key={h} color="white" borderColor={ROJO}>{h}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>{filas}</Tbody>
                  </Table>
                </Box>
              );
            })()}
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default Dashboard;
